/**
 * Step methods ehq-web no longer has (EHQ-514).
 *
 * EHQ-10 dropped these from the step API. Without a stub, saved step code that
 * still calls one fails with only the method's name in Python
 * ("Execution error: save_project_file") or "not a function" in JavaScript.
 * Both bridges install a stub for each that throws `removedMethodMessage`, so
 * the step says what happened and, where there is one, what to use instead.
 */

export type StepLanguage = 'javascript' | 'python';

export interface RemovedStepMethod {
  /** The global it lived on: `EHQ`, `ARROW_FLIGHT` or `convert`. */
  readonly namespace: 'EHQ' | 'ARROW_FLIGHT' | 'convert';
  /** camelCase name; Python's snake_case alias is derived from it. */
  readonly method: string;
  /** The call to use instead, per language, when there is one. */
  readonly replacement?: Readonly<Record<StepLanguage, string>>;
  /** Why there is no replacement, when the reason helps the author. */
  readonly note?: string;
}

/** EHQ-171 removed parquet-wasm, and headless runs never had these either. */
const PARQUET_NOTE = 'Browser-side Parquet support is no longer available, and there is no in-browser replacement.';

export const REMOVED_STEP_METHODS: readonly RemovedStepMethod[] = [
  {
    namespace: 'EHQ',
    method: 'saveProjectFile',
    replacement: { javascript: 'EHQ.saveFile(fileName, content)', python: 'ehq.save_file(file_name, content)' },
  },
  { namespace: 'EHQ', method: 'removeFile' },
  { namespace: 'EHQ', method: 'listProjectFiles' },
  {
    namespace: 'EHQ',
    method: 'fetchJson',
    replacement: { javascript: 'EHQ.fetch(url) and parse the JSON', python: 'ehq.fetch(url) and parse the JSON' },
  },
  {
    namespace: 'EHQ',
    method: 'showAlert',
    replacement: { javascript: 'EHQ.showSimpleAlert(message)', python: 'ehq.show_simple_alert(message)' },
  },
  {
    namespace: 'EHQ',
    method: 'showPrompt',
    replacement: { javascript: 'EHQ.showTextPrompt(message)', python: 'ehq.show_text_prompt(message)' },
  },
  { namespace: 'EHQ', method: 'tensorToCanvas' },
  { namespace: 'EHQ', method: 'getAuthToken' },
  { namespace: 'EHQ', method: 'getApiUrl' },
  { namespace: 'ARROW_FLIGHT', method: 'performSqlQuery' },
  { namespace: 'ARROW_FLIGHT', method: 'registerSchema' },
  { namespace: 'ARROW_FLIGHT', method: 'saveSqlQueryAsTable' },
  { namespace: 'convert', method: 'csvToArrow' },
  { namespace: 'convert', method: 'csvToJSON' },
  { namespace: 'convert', method: 'jsonToCSV' },
  { namespace: 'convert', method: 'arrowToParquet', note: PARQUET_NOTE },
  { namespace: 'convert', method: 'parquetToArrow', note: PARQUET_NOTE },
];

/** camelCase to snake_case, as the Python bridge aliases methods. */
export function toSnakeCase(name: string): string {
  return name.replaceAll(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

/** The method as a step in `language` would write it. */
function stepName(removed: RemovedStepMethod, language: StepLanguage): string {
  if (language === 'javascript') return `${removed.namespace}.${removed.method}`;
  const namespace = removed.namespace === 'EHQ' ? 'ehq' : removed.namespace;
  return `${namespace}.${toSnakeCase(removed.method)}`;
}

/** What a call to a removed method throws. */
export function removedMethodMessage(removed: RemovedStepMethod, language: StepLanguage): string {
  const parts = [`${stepName(removed, language)} was removed from the step API.`];
  if (removed.replacement) parts.push(`Use ${removed.replacement[language]} instead.`);
  if (removed.note) parts.push(removed.note);
  return parts.join(' ');
}
